import type { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import { LayoutColumn } from './nodes/LayoutColumn.tsx';
import type { NodeComponentsProps } from '@atlaskit/renderer/types';

import { Blockquote } from './nodes/Blockquote.tsx';
import { BulletList } from './nodes/BulletList.tsx';
import { CodeBlock } from './nodes/CodeBlock.tsx';
import { Date } from './nodes/Date.tsx';
import { Emoji } from './nodes/Emoji.tsx';
import { Heading } from './nodes/Heading.tsx';
import { InlineCard } from './nodes/InlineCard.tsx';
import { LayoutSection } from './nodes/LayoutSection.tsx';
import { ListItem } from './nodes/ListItem.tsx';
import { Media } from './nodes/Media.tsx';
import { MediaSingle } from './nodes/MediaSingle.tsx';
import { Mention } from './nodes/Mention.tsx';
import { OrderedList } from './nodes/OrderedList.tsx';
import { Panel } from './nodes/Panel.tsx';
import { PanelC1 } from './nodes/PanelC1.tsx';
import { Paragraph } from './nodes/Paragraph.tsx';
import { Status } from './nodes/Status.tsx';
import { Table } from './nodes/table/Table.tsx';
import { TableCell, TableHeader } from './nodes/table/TableCell.tsx';
import { TableRow } from './nodes/table/TableRow.tsx';
import { TaskItem } from './nodes/TaskItem.tsx';
import { TaskList } from './nodes/TaskList.tsx';

export type LightNodeName = keyof Pick<
	typeof defaultSchema.nodes,
	| 'blockquote'
	| 'bulletList'
	| 'codeBlock'
	| 'date'
	| 'emoji'
	| 'heading'
	| 'inlineCard'
	| 'layoutColumn'
	| 'layoutSection'
	| 'listItem'
	| 'media'
	| 'mediaSingle'
	| 'mention'
	| 'orderedList'
	| 'panel'
	| 'panel_c1'
	| 'paragraph'
	| 'status'
	| 'table'
	| 'tableCell'
	| 'tableHeader'
	| 'tableRow'
	| 'taskItem'
	| 'taskList'
>;

export const lightNodeComponents: Pick<NodeComponentsProps, LightNodeName> = {
	blockquote: Blockquote,
	bulletList: BulletList,
	codeBlock: CodeBlock,
	date: Date,
	emoji: Emoji,
	heading: Heading,
	inlineCard: InlineCard,
	// Tables make their entire layout section ineligible for light rendering.
	layoutColumn: LayoutColumn,
	layoutSection: LayoutSection,
	listItem: ListItem,
	media: Media,
	mediaSingle: MediaSingle,
	mention: Mention,
	orderedList: OrderedList,
	panel: Panel,
	panel_c1: PanelC1,
	paragraph: Paragraph,
	status: Status,
	table: Table,
	tableCell: TableCell,
	tableHeader: TableHeader,
	tableRow: TableRow,
	taskItem: TaskItem,
	taskList: TaskList,
};

export const lightSupportedNodeNames: readonly LightNodeName[] = Object.freeze(
	Object.keys(lightNodeComponents) as LightNodeName[],
);

// Table virtualization opts into these; without it, tables keep their subtree full.
export const lightTableNodeNames: readonly LightNodeName[] = Object.freeze([
	'table',
	'tableCell',
	'tableHeader',
	'tableRow',
]);
